
import { Hole } from "@/types";
import { cn } from "@/lib/utils";
import { Flag, FlagTriangleRight, Star } from "lucide-react";
import { HoleTileData, GolferBirdie } from "@/integrations/supabase/scores";
import AvatarPlaceholder from "@/components/ui/avatar-placeholder";

interface HoleGridProps {
  holes: Hole[];
  holeTileData: Map<number, HoleTileData>;
  activeGolferId: string | "all";
  onHoleClick: (holeNumber: number) => void;
}

interface HoleTileProps {
  hole: Hole;
  tileData?: HoleTileData;
  activeGolferId: string | "all";
  onClick: () => void;
}

const HoleTile = ({ hole, tileData, activeGolferId, onClick }: HoleTileProps) => {
  const allGolferBirdies = tileData?.golferBirdies || [];

  // Determine if tile is green based on the active filter
  const isBirdied = activeGolferId === "all"
    ? allGolferBirdies.length > 0
    : allGolferBirdies.some(g => g.userId === activeGolferId);

  // Get best score type for the filtered view (controls tile styling)
  const filteredBestType = activeGolferId === "all"
    ? tileData?.bestScoreType
    : allGolferBirdies.find(g => g.userId === activeGolferId)?.bestScoreType;

  // Score type visual styling
  let tileBg = "bg-white";
  let iconElement: React.ReactNode;
  let ringClass = "";

  if (isBirdied) {
    switch (filteredBestType) {
      case "double_eagle":
        tileBg = "bg-yellow-100";
        ringClass = "ring-2 ring-yellow-500";
        iconElement = <Star className="h-5 w-5 text-yellow-600 animate-bounce-small" />;
        break;
      case "eagle":
        tileBg = "bg-amber-100";
        ringClass = "ring-2 ring-amber-400";
        iconElement = <FlagTriangleRight className="h-5 w-5 text-amber-600 animate-bounce-small" />;
        break;
      default:
        tileBg = "bg-green-200";
        iconElement = <FlagTriangleRight className="h-5 w-5 text-green-700 animate-bounce-small" />;
        break;
    }
  } else {
    iconElement = <Flag className="h-5 w-5 text-forest" />;
  }

  return (
    <div
      onClick={onClick}
      className={cn(
        "relative border border-forest rounded-lg cursor-pointer transition-all duration-200 ease-in-out overflow-hidden h-24",
        tileBg,
        ringClass,
        isBirdied && "shadow-md"
      )}
    >
      <div className="p-2 flex flex-col items-center justify-center">
        <div className="flex items-center justify-center gap-1 mb-1">
          {iconElement}
          <span className="font-bold text-xl text-forest">
            {hole.number}
          </span>
        </div>

        <span className="text-xs text-forest">Par {hole.par}</span>
      </div>

      {/* Golfer avatars — always show ALL golfers who birdied regardless of filter */}
      <div className={cn(
        "flex flex-wrap justify-center gap-1 p-1 border-t border-forest/20 bg-white/80 mt-auto",
        allGolferBirdies.length === 0 && "invisible"
      )}>
        {allGolferBirdies.length > 0 ? (
          allGolferBirdies.map((golfer: GolferBirdie) => (
            <AvatarPlaceholder
              key={golfer.userId}
              user={{
                id: golfer.userId,
                name: golfer.name,
                imageUrl: golfer.imageUrl,
              }}
              size="sm"
              className="h-5 w-5"
            />
          ))
        ) : (
          <div className="h-5 w-5 opacity-0" />
        )}
      </div>
    </div>
  );
};

const HoleGrid = ({ holes, holeTileData, activeGolferId, onHoleClick }: HoleGridProps) => {
  const front9 = holes.filter(h => h.number <= 9);
  const back9 = holes.filter(h => h.number > 9);

  const renderSection = (sectionHoles: Hole[]) => (
    <div className="grid grid-cols-3 lg:grid-cols-9 gap-2 sm:gap-4">
      {sectionHoles.map((hole) => (
        <HoleTile
          key={hole.number}
          hole={hole}
          tileData={holeTileData.get(hole.number)}
          activeGolferId={activeGolferId}
          onClick={() => onHoleClick(hole.number)}
        />
      ))}
    </div>
  );

  return (
    <div className="mb-6 space-y-4">
      {front9.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-forest uppercase tracking-wide">Front 9</span>
            <div className="flex-1 h-px bg-forest/20" />
          </div>
          {renderSection(front9)}
        </div>
      )}
      {back9.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-forest uppercase tracking-wide">Back 9</span>
            <div className="flex-1 h-px bg-forest/20" />
          </div>
          {renderSection(back9)}
        </div>
      )}
    </div>
  );
};

export default HoleGrid;
